import type { QueryClient } from "@tanstack/react-query";

// One TanStack cache serves every org the app visits in one page lifetime, and the
// default staleTime is Infinity. On the iPhone/Android shell the WebView lives until
// force-quit, so a buyer who opens vendor A and then vendor B keeps A's cached reads.
// A key that does not name the org ("/api/categories") is then served on B's page
// with A's data — org `demo`'s categories and market report on Pitt's page
// (Jon, 2026-10-24); a custom-URL account at the wrong vendor (#260, 2026-10-08).
//
// The guard drops every cached query the moment the resolved org changes, so no
// page can render another org's data whatever its keys look like. The first org
// seen clears nothing. Queries only: in-flight mutations keep their callbacks.
export function createOrgCacheGuard(queryClient: QueryClient): (orgSlug: string) => boolean {
  let current: string | null = null;
  return (orgSlug: string) => {
    const changed = current !== null && current !== orgSlug;
    current = orgSlug;
    if (changed) queryClient.removeQueries();
    return changed;
  };
}

// On an org change the clear stays UNFILTERED on purpose: admin pages cache ~200 keys
// that do not name the org, and this clear is what keeps them from serving one org's
// data on another org's pages.

// A customer signed out (sign-out button, kiosk hand-back, a session that no longer
// owns the page). The next person on this device starts with none of that buyer's
// account, orders or menu in memory.
//
// Forgotten AFTER the ordering page has gone, never while it is still mounted:
// removeQueries() under live observers rebuilds and refetches every one of them at
// once for the just-signed-out account — a burst of 401s, error states and an empty
// menu flashing before the redirect. So the sign-out only records what to forget
// (`forgetCustomerDataOnLeave`), and OrganizationProvider, which sits above every
// route, forgets it once the route has changed (`flushForgottenCustomerData`): only
// queries that name that org (every customer-page key does — orgSlug, or a getApiPath string carrying /org/<slug>/)
// and that no mounted page is still reading. Admin caches are left alone.
let pendingForget: string | null = null;

export function forgetCustomerDataOnLeave(orgSlug: string | undefined): void {
  pendingForget = orgSlug ?? "";
}

export function flushForgottenCustomerData(queryClient: QueryClient): void {
  if (pendingForget === null) return;
  const slug = pendingForget;
  pendingForget = null;
  queryClient.removeQueries({
    predicate: (query) =>
      query.getObserversCount() === 0
      && (slug === "" || query.queryKey.some((part) => part === slug || (typeof part === "string" && part.includes(`/org/${slug}/`)))),
  });
}

/** Tests only. */
export function hasPendingForget(): boolean {
  return pendingForget !== null;
}
